import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { useDeleteRouteVersion } from "../hooks";
import type { RouteVersion } from "../types";
import { apiErr } from "../utils";

type Version = RouteVersion & { variantLabel: string; variantBinding?: boolean };

/**
 * "UWAGA" confirmation for deleting one route version (map tab). Spells out what goes
 * with the version, which version takes over as the variant's current one, and — the
 * point of the warning — that deleting the variant's LAST version deletes the whole
 * variant. The server gate (koordynator projektu / dyrektor techniczny / CEO) is
 * mirrored by RouteVariant.can_delete, which decides whether the control shows at all.
 */
export function DeleteRouteVersionModal({
  connection,
  version,
  successor,
  singleVariant,
  onClose,
  onDeleted,
}: {
  connection: string;
  // Version staged for deletion; null = modal closed.
  version: Version | null;
  // Newest other version of the same variant — becomes current when `version` was.
  // null = `version` is the variant's last one and the variant goes with it.
  successor: Version | null;
  // One variant only → it is binding automatically, so its current version is the
  // project's authoritative route even without the flag.
  singleVariant: boolean;
  onClose: () => void;
  onDeleted: (version: Version, variantDeleted: boolean) => void;
}) {
  const del = useDeleteRouteVersion(connection);
  // Server-side refusal (403 for anyone but the allowed roles, 400 while formatki are
  // generating on the version) shown inside the modal instead of dying silently.
  const [error, setError] = useState<string | null>(null);
  const close = () => {
    setError(null);
    onClose();
  };
  const lastInVariant = version != null && successor == null;
  const authoritative =
    !!version && version.is_current && (singleVariant || !!version.variantBinding);

  return (
    <Modal open={!!version} onClose={close} title="UWAGA" size="sm">
      {version && (
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <AlertTriangle size={24} className="shrink-0 mt-0.5 text-[var(--color-danger)]" />
            <div className="space-y-2 text-sm text-[var(--color-text-secondary)]">
              <p className="font-bold text-[var(--color-text-primary)]">
                Zamierzasz trwale usunąć wersję trasy {version.label}.
              </p>
              <p>
                Ta operacja <strong>nieodwracalnie</strong> skasuje plik DXF tej wersji,
                jej podgląd, wyznaczone odcinki na działkach oraz wygenerowane z niej
                formatki planu sytuacyjnego. Powiązania uzgodnień z właścicielami dla tej
                wersji zostaną wyczyszczone.
              </p>
              {lastInVariant ? (
                <p className="font-semibold text-[var(--color-danger)]">
                  <AlertTriangle size={14} className="inline -mt-0.5 mr-1" />
                  To ostatnia wersja wariantu {version.variantLabel} — usunięcie jej usunie
                  też cały wariant {version.variantLabel}.
                </p>
              ) : version.is_current && successor ? (
                <p>
                  To bieżąca wersja wariantu {version.variantLabel} — po usunięciu bieżącą
                  stanie się wersja {successor.label}.
                </p>
              ) : null}
              {authoritative && (
                <p className="font-semibold text-[var(--color-danger)]">
                  Z tej wersji liczą się obecnie statusy działek, mapy zakresu i sprawy —
                  po usunięciu zostaną przeliczone.
                </p>
              )}
              <p className="font-semibold text-[var(--color-text-primary)]">
                Tej operacji nie można cofnąć.
              </p>
            </div>
          </div>
          {error && <p className="text-xs font-mono text-[var(--color-danger)]">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={close} disabled={del.isPending}>
              Anuluj
            </Button>
            <Button
              variant="danger"
              loading={del.isPending}
              onClick={() => {
                setError(null);
                del.mutate(version.id, {
                  onSuccess: () => {
                    setError(null);
                    onDeleted(version, lastInVariant);
                  },
                  onError: (e) => setError(apiErr(e, "Nie udało się usunąć wersji trasy.")),
                });
              }}
            >
              {lastInVariant
                ? `Usuń wersję ${version.label} i wariant ${version.variantLabel}`
                : `Usuń wersję ${version.label}`}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
